import {
  Box,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import iconMap from "../../../paginas/gestao/menu/iconmap";
import tokenService from "../../../services/tokenservice";
import { useEasyBrowserContext } from "../browser";
import { MenuItemType } from "../taskbar/taskbartypes.d";

const HISTORY_KEY = "findtaskbar_history";
const MAX_HISTORY = 5;

const FindTaskBar = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
  const { abrirPagina } = useEasyBrowserContext()!;

  const [allScreens, setAllScreens] = useState<MenuItemType[]>([]);
  const [results, setResults] = useState<MenuItemType[]>([]);
  const [history, setHistory] = useState<MenuItemType[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const inputRef = useRef<HTMLInputElement | null>(null);

  // ---- Carrega o histórico do localStorage ----
  useEffect(() => {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (raw) {
      setHistory(JSON.parse(raw));
    }
  }, []);

  // ---- Salva histórico quando mudar ----
  const salvarHistorico = (lista: MenuItemType[]) => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(lista));
  };

  // ---- Adiciona item ao histórico ----
  const registrarHistorico = (item: MenuItemType) => {
    const novo = [item, ...history.filter((h) => h.id !== item.id)].slice(
      0,
      MAX_HISTORY
    );
    setHistory(novo);
    salvarHistorico(novo);
  };

  // Carregar todas as telas
  useEffect(() => {
    const loadMenu = async () => {
      const menu = await tokenService.getMenu();
      const parsed = JSON.parse(menu);
      const items: MenuItemType[] = parsed.menus || [];
      setAllScreens(items);
    };
    loadMenu();
  }, []);

  // Filtrar assim que digitar
useEffect(() => {
  const termo = query.trim().toLowerCase();

  if (!termo) {
    setResults([]);
    return;
  }

  const filtrado = allScreens.filter((item) => {
    const descricao = item.descricao?.toLowerCase() ?? "";
    const id = item.id?.toLowerCase() ?? "";
    const grupo = item.grupo?.join(" ").toLowerCase() ?? "";

    return (
      descricao.includes(termo) ||
      id.includes(termo) ||
      grupo.includes(termo)
    );
  });

  setResults(filtrado);
  mostrarSugestoes();
}, [query, allScreens]);


  const abrir = (item: MenuItemType) => {
    abrirPagina(item);
    registrarHistorico(item); // grava no histórico
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const mostrarSugestoes = () => {
    if (query.trim().length > 0) {
      setOpen(results.length > 0);
    } else {
      // Sem digitar nada → mostrar histórico
      setOpen(history.length > 0);
    }
  };

  const itensParaMostrar =
    query.trim().length > 0 ? results : history;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        sx={{
          display: { sx: "none", md: "flex" },
          alignItems: "center",
          height: "30px",
          width: "250px",
          position: "relative",
          overflow: "hidden",
          borderRadius: 1,
          borderRight: "2px solid rgba(0,0,0,0.1)",
          borderBottom: "2px solid rgba(0,0,0,0.1)",
          bgcolor: "var(--color-suave)",
          px: 1,

          ":hover": {
            cursor: "pointer",
            bgcolor: "var(--cor-destaque)",
          },
        }}
      >
        <TextField
          inputRef={inputRef}
          placeholder="Buscar telas"
          variant="standard"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={mostrarSugestoes}
          autoComplete="off"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "14px",
              padding: 0,
              height: "30px",
              color: "black",

              "& .MuiInputBase-input": {
                padding: 0,
                height: "30px",
                lineHeight: "30px",
              },

              "&::placeholder": {
                color: "#555",
              },

              "&:hover": {
                cursor: "text",
              },
            },
          }}
        />

        <Popper
          open={open}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ zIndex: 2000 }}
        >
          <Paper
            elevation={4}
            sx={{
              width: inputRef.current?.offsetWidth || 200,
              maxHeight: 250,
              overflowY: "auto",
            }}
          >
            <List dense>
              {itensParaMostrar.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => abrir(item)}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {iconMap[item.icone] ?? null}
                  <ListItemText
                    primary={item.descricao}
                    primaryTypographyProps={{
                      fontSize: "14px",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
});

export default FindTaskBar;
